import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Search, Loader2, Eye, Mail, Phone, Building2, MapPin, Calendar, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-500' },
  { value: 'quoted', label: 'Quoted', color: 'bg-purple-500' },
  { value: 'closed', label: 'Closed', color: 'bg-gray-500' },
];

const getStatusMeta = (status) => STATUSES.find((s) => s.value === status) || { label: status, color: 'bg-gray-400' };

export const AdminQuotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, quoted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (search) params.search = search;
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/quotations/`, { params }),
        axios.get(`${API}/quotations/stats/summary`),
      ]);
      setQuotations(listRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchQuotations, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/quotations/${id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      fetchQuotations();
      if (selected?.id === id) setSelected({ ...selected, status: newStatus });
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const openWhatsApp = (phone) => {
    const cleaned = (phone || '').replace(/\D/g, '');
    if (cleaned) window.open(`https://wa.me/${cleaned}`, '_blank');
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('en-MY', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-blue-900">Quotation Requests</h2>
        <p className="text-gray-600 text-sm">Manage customer quotation submissions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={stats.total} color="bg-blue-900" />
        <StatCard label="New" value={stats.new} color="bg-blue-500" />
        <StatCard label="In Progress" value={stats.in_progress} color="bg-yellow-500" />
        <StatCard label="Quoted" value={stats.quoted} color="bg-purple-500" />
        <StatCard label="Closed" value={stats.closed} color="bg-gray-500" />
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search by name, company, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-quotations-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-56" data-testid="filter-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-blue-900" size={40} />
        </div>
      ) : quotations.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border">
          <p className="text-gray-500">No quotations found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Company</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Products</th>
                  <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotations.map((q) => {
                  const meta = getStatusMeta(q.status);
                  return (
                    <tr key={q.id} className="border-t hover:bg-gray-50" data-testid={`quotation-row-${q.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-blue-900">{q.contact_person}</p>
                        <p className="text-xs text-gray-500">{q.email}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="text-gray-800">{q.company_name}</p>
                        <p className="text-xs text-gray-500">{q.phone}</p>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(q.product_categories || []).slice(0, 3).map((c, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                          ))}
                          {(q.product_categories?.length || 0) > 3 && (
                            <Badge variant="outline" className="text-xs">+{q.product_categories.length - 3}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-gray-600 text-xs">
                        {formatDate(q.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${meta.color} text-white`}>{meta.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelected(q)}
                          data-testid={`view-quotation-${q.id}`}
                        >
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selected.contact_person}</DialogTitle>
                <DialogDescription>
                  Submitted {formatDate(selected.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {/* Status control */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <p className="text-xs font-semibold text-blue-900 mb-2">STATUS</p>
                  <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <Button
                        key={s.value}
                        size="sm"
                        onClick={() => updateStatus(selected.id, s.value)}
                        className={`${
                          selected.status === s.value
                            ? `${s.color} text-white`
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`status-btn-${s.value}`}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Contact info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoRow icon={Building2} label="Company" value={selected.company_name} />
                  <InfoRow icon={Mail} label="Email" value={selected.email} link={`mailto:${selected.email}`} />
                  <InfoRow icon={Phone} label="Phone" value={selected.phone} link={`tel:${selected.phone}`} />
                  <InfoRow icon={MapPin} label="Delivery Location" value={selected.delivery_location} />
                </div>

                {/* Products */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">PRODUCT CATEGORIES REQUESTED</p>
                  <div className="flex flex-wrap gap-2">
                    {(selected.product_categories || []).map((c, i) => (
                      <Badge key={i} className="bg-green-100 text-green-800 border-green-200">{c}</Badge>
                    ))}
                  </div>
                </div>

                {/* Message */}
                {selected.message && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">MESSAGE / NOTES</p>
                    <p className="bg-gray-50 rounded-lg p-4 border text-gray-800 whitespace-pre-wrap">
                      {selected.message}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-2 border-t">
                  <Button
                    onClick={() => openWhatsApp(selected.phone)}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    data-testid="wa-customer-btn"
                  >
                    <MessageCircle size={16} className="mr-2" /> WhatsApp Customer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`mailto:${selected.email}`, '_blank')}
                    data-testid="email-customer-btn"
                  >
                    <Mail size={16} className="mr-2" /> Email Customer
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className={`${color} text-white rounded-lg p-4 shadow-sm`}>
    <p className="text-xs uppercase tracking-wide opacity-90">{label}</p>
    <p className="text-3xl font-bold mt-1">{value}</p>
  </div>
);

const InfoRow = ({ icon: Icon, label, value, link }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 mb-1 flex items-center">
      <Icon size={12} className="mr-1" />
      {label}
    </p>
    {link ? (
      <a href={link} className="text-blue-900 hover:underline font-medium break-all">{value}</a>
    ) : (
      <p className="text-blue-900 font-medium">{value || '-'}</p>
    )}
  </div>
);
